import { useContext, useEffect, useRef, useState, type ReactNode } from "react";
import {
  CANONICAL_MINTS,
  formatNative,
  type AutoReopenPhase,
  type AutoReopenResponse,
  type AutoReopenStatus,
  type OperationFailure,
  type MarketMovement,
  type RetryWithSlippageQuoteResponse,
  type RetryWithSlippageResponse,
  type RetryDiagnosisResponse,
} from "@auto-pools/core";
import { api, ApiError } from "../api/client";
import { RuntimeStatusContext } from "../api/runtimeStatusContext";
import { useViewer } from "../auth/authContext";
import { orientedBounds, orientedPrice } from "../lib/priceFrame";
import { formatFundingUsd, formatToken, formatPrice } from "../lib/format";
import { formatAgo, formatWhen, shorten } from "../lib/format";
import type { Operation } from "../types";
import {
  automaticExitLine,
  currentFailure,
  ENGINE_OWNED_EXITS,
  failurePresentation,
  operationPresentation,
  operationSteps,
  routeFailureLine,
  recommendsSetup,
} from "../lib/operationPresentation";

const factLabelStyle = { fontSize: 11 } as const;
const factValueStyle = {
  margin: 0,
  color: "var(--text)",
  fontSize: 13,
  fontWeight: 500,
} as const;
const factNoteStyle = {
  color: "var(--faint)",
  fontFamily: "var(--font-mono)",
  fontSize: 11,
} as const;
/** The stylesheet is not part of this change; the line matches the muted notes around it. */
const automaticExitStyle = { margin: "3px 0 0", color: "var(--muted)", fontSize: 12 } as const;
const reviewFactValueStyle = { ...factValueStyle, fontSize: 15 } as const;
const bpsLabel = (value: number) => `${formatNative(BigInt(value), 2)}%`;
const percentLabel = (value: string) => {
  const numeric = Number(value);
  return Number.isFinite(numeric)
    ? `${new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(numeric)}%`
    : "—";
};

interface QuoteObservation {
  subject: object;
  quote: RetryWithSlippageQuoteResponse | null;
  error: string | null;
  failure: OperationFailure | null;
  failureBaselineAt: string | null;
}

interface DiagnosisObservation {
  result: RetryDiagnosisResponse["result"];
  message: string;
  checkedAt: string;
  failureBaselineAt: string | null;
  failure: OperationFailure | null;
}

function diagnosisOperationKey(op: Operation) {
  return JSON.stringify([
    op.id,
    op.positionId ?? null,
    op.intentRevision?.revision ?? null,
    op.intentRevision?.activeIntentHash ?? null,
    op.step,
    op.state,
    op.attempts,
  ]);
}

function diagnosisFailure(response: RetryDiagnosisResponse): OperationFailure | null {
  const failure = currentFailure(response.operation);
  if (!failure) return null;
  if (
    response.result === "deposit-price-guard" &&
    failure.simulationReason === "PRICE_SLIPPAGE_OUT_OF_BOUNDS"
  )
    return failure;
  if (
    response.result === "swap-output" &&
    failure.priceChanged?.reason === "EXPECTED_OUTPUT_BELOW_APPROVED"
  )
    return failure;
  if (response.result === "price-drift" && failure.priceChanged?.reason === "PRICE_DRIFT")
    return failure;
  return null;
}

function Fact({ label, value, note }: { label: string; value: ReactNode; note?: ReactNode }) {
  return (
    <div>
      <dt style={factLabelStyle}>{label}</dt>
      <dd className="num" style={factValueStyle}>
        {value}
      </dd>
      {note && <small style={factNoteStyle}>{note}</small>}
    </div>
  );
}

function MovementFacts({
  movement,
  opening,
}: {
  movement: MarketMovement;
  opening: Operation["opening"];
}) {
  const tokens = opening?.prefill ? [opening.prefill.tokenA, opening.prefill.tokenB] : [];
  const approved = orientedPrice(
    { price: movement.approvedPrice, baseMint: movement.baseMint, quoteMint: movement.quoteMint },
    tokens,
  );
  const observed = orientedPrice(
    { price: movement.observedPrice, baseMint: movement.baseMint, quoteMint: movement.quoteMint },
    tokens,
  );
  // The unit comes from the same producer as the prices, so it can never name the other
  // orientation; with neither price usable there is no orientation to name, so no unit is shown.
  const unit = approved?.unit ?? observed?.unit ?? null;
  return (
    <section aria-label="Measured market movement">
      <p>Pool price moved {percentLabel(movement.absoluteChangePercent)} since approval.</p>
      <p className="num">
        {approved ? formatPrice(approved.price) : "—"} →{" "}
        {observed ? formatPrice(observed.price) : "—"}
        {unit !== null && (
          <>
            {" "}
            <span title={`Mints: ${movement.baseMint}, ${movement.quoteMint}`}>{unit}</span>
          </>
        )}
      </p>
      <small>Observed {formatWhen(movement.obtainedAt)}</small>
      <details>
        <summary>About this price</summary>
        <p>
          Pre-simulation pool read, not the failed transaction's execution price or the required
          slippage.
        </p>
      </details>
    </section>
  );
}

const AUTO_REOPEN_STEPS: [AutoReopenPhase, string][] = [
  ["cancelling", "Cancelling"],
  ["getting-price", "Getting latest price"],
  ["opening", "Opening"],
  ["completed", "Completed"],
];
const AUTO_REOPEN_RUNNING: Partial<Record<AutoReopenPhase, string>> = {
  cancelling: "Cancelling the opening…",
  "getting-price": "Getting the latest price…",
  opening: "Opening the new position…",
};

/** A sequence's own progress replaces the cleanup success view, so "Opening cancelled" never shows. */
function replacesCleanupSuccess(op: Operation) {
  return !!op.autoReopen && op.step === "cleanup" && op.state === "completed";
}

function autoReopenChip(sequence: AutoReopenStatus) {
  if (sequence.phase === "completed") return "Reopened";
  if (sequence.phase === "needs-action") return "Reopen · needs action";
  return sequence.automationActive ? "Reopening" : "Reopening · paused";
}

/** Server status is the only source: no phase, reason or slippage is inferred in the browser. */
function AutoReopenProgress({ sequence }: { sequence: AutoReopenStatus }) {
  const stopped = sequence.phase === "needs-action",
    done = sequence.phase === "completed";
  const paused = !stopped && !done && !sequence.automationActive;
  const index = AUTO_REOPEN_STEPS.findIndex(([phase]) => phase === sequence.phase);
  const title = stopped
    ? "Automatic reopen stopped"
    : done
      ? "Reopened around the current price"
      : paused
        ? "Automatic reopen is paused"
        : (AUTO_REOPEN_RUNNING[sequence.phase] ?? "Reopening");
  const message = stopped
    ? (sequence.reason ?? "The server recorded no reason for this stop.")
    : done
      ? "The replacement opening completed."
      : paused
        ? "Nothing continues until the server can authorize the next step again."
        : "The server is running this sequence. You can leave this page.";
  return (
    <section className="auto-reopen-progress" aria-label="Automatic reopen">
      <ol className="operation-stepper">
        {AUTO_REOPEN_STEPS.map(([phase, label], position) => {
          const state = done
            ? "done"
            : stopped
              ? "pending"
              : position < index
                ? "done"
                : position === index
                  ? "current"
                  : "pending";
          return (
            <li key={phase} className={state}>
              <span aria-hidden="true">{state === "done" ? "✓" : ""}</span>
              {label}
            </li>
          );
        })}
      </ol>
      <div className="operation-message">
        <span
          className={`operation-message-icon ${!stopped && !done && !paused ? "spinner" : ""}`}
          aria-hidden="true"
        >
          {stopped ? "!" : done ? "✓" : paused ? "…" : ""}
        </span>
        <div>
          <strong style={{ fontSize: 14 }}>{title}</strong>
          <p>{message}</p>
        </div>
      </div>
      {stopped && sequence.reasonCode && <small>Reason code: {sequence.reasonCode}</small>}
      {/* Unavailable evidence must never read as "no slippage", whether it arrives as null or 0. */}
      {typeof sequence.selectedSlippageBps === "number" &&
      sequence.reasonCode !== "EVIDENCE_UNAVAILABLE" ? (
        <small>Approved reopen slippage: {bpsLabel(sequence.selectedSlippageBps)}</small>
      ) : (
        <p className="operation-action-reason">
          The approved reopen slippage cannot be read from the recorded sequence.
        </p>
      )}
    </section>
  );
}

export function OperationStatus({
  operation,
  onResume,
  compact,
  onCancel,
  onRestart,
  onChanged,
  actionPending = false,
}: {
  operation: Operation;
  onResume?: (operation: Operation) => void;
  compact?: boolean;
  onCancel?: () => void;
  onRestart?: () => void;
  onChanged?: () => void;
  actionPending?: boolean;
}) {
  const view = operationPresentation(operation);
  const sequence = operation.autoReopen ?? null;
  return (
    <article className={compact ? "operation-inline" : `operation-row ${view.tone}`}>
      {!compact && (
        <div className="operation-title">
          <strong>
            {operation.kind === "open" ? "Open" : operation.step === "cleanup" ? "Cancel" : "Close"}{" "}
            operation
          </strong>
          <span className={`tag ${view.tone}`}>
            {sequence && replacesCleanupSuccess(operation) ? autoReopenChip(sequence) : view.chip}
          </span>
        </div>
      )}
      <CompactOperation
        operation={operation}
        onResume={onResume}
        onCancel={onCancel}
        onRestart={onRestart}
        onChanged={onChanged}
        actionPending={actionPending}
      />
    </article>
  );
}

function CompactOperation({
  operation: op,
  onResume,
  onCancel,
  onRestart,
  onChanged,
  actionPending,
}: {
  operation: Operation;
  onResume?: (operation: Operation) => void;
  onCancel?: () => void;
  onRestart?: () => void;
  onChanged?: () => void;
  actionPending: boolean;
}) {
  const runtime = useContext(RuntimeStatusContext);
  // Retry, cancel, reopen and diagnosis are commands: a viewer sees the state, not the choices.
  const viewer = useViewer();
  const retry = op.retry,
    opening = op.kind === "open";
  const quoteIdentity = JSON.stringify([
    op.id,
    op.positionId,
    op.kind,
    op.intentRevision?.activeIntentHash,
    op.intentRevision?.revision,
    op.step,
    op.state,
    op.attempts,
    op.maxAttempts,
    runtime?.data?.wallet.address,
    runtime?.data?.wallet.generation,
  ]);
  const [selectionIdentity, setSelectionIdentity] = useState(quoteIdentity);
  const [choice, setChoice] = useState(""),
    [editedBps, setBps] = useState<string | null>(null);
  const [reopenChecked, setReopenChecked] = useState(false);
  const bps =
    editedBps ??
    (retry?.suggestedSlippageBps ? formatNative(BigInt(retry.suggestedSlippageBps), 2) : "");
  // A different operation or wallet starts with its own server suggestion.
  if (selectionIdentity !== quoteIdentity) {
    setSelectionIdentity(quoteIdentity);
    setChoice("");
    setBps(null);
    setReopenChecked(false);
  }
  const [quoteObservation, setQuoteObservation] = useState<QuoteObservation | null>(null);
  const [quotePending, setQuotePending] = useState(false);
  const quoteFlight = useRef<object | null>(null);
  const [pending, setPending] = useState(false),
    [error, setError] = useState<string | null>(null);
  const [reopenUncertain, setReopenUncertain] = useState(false);
  // One pinned request survives polling identity changes; only a definite rejection releases it.
  const reopenAttempt = useRef<{ idempotencyKey: string; maxSlippageBps: number } | null>(null);
  const [reportedFailure, setReportedFailure] = useState<OperationFailure | null>(null);
  const [diagnosis, setDiagnosis] = useState<DiagnosisObservation | null>(null),
    [diagnosisPending, setDiagnosisPending] = useState(false),
    [diagnosisError, setDiagnosisError] = useState<string | null>(null);
  const serverFailure = currentFailure(op);
  const currentDiagnosis =
    diagnosis &&
    (!serverFailure ||
      (diagnosis.failureBaselineAt !== null &&
        Date.parse(diagnosis.failureBaselineAt) >= Date.parse(serverFailure.observedAt)))
      ? diagnosis
      : null;
  const observedFailure = currentDiagnosis
    ? currentDiagnosis.result === "passed"
      ? null
      : currentDiagnosis.failure
    : serverFailure;
  const view = operationPresentation(op, observedFailure);
  const failure = view.failure;
  const depositGuard = failure?.simulationReason === "PRICE_SLIPPAGE_OUT_OF_BOUNDS";
  // While the engine still owns a stop-triggered exit, no surface may tell the operator to act: a
  // manual resume CLOSES the position, which is the early exit letting the stop manage it was
  // meant to avoid. This gates on the durable state rather than on `view.mode`, deliberately —
  // `view.mode` is derived from failure codes and the operation's step, and three review rounds
  // showed that two independent derivations of "what should the operator do" cannot be kept
  // consistent by ordering branches. `attention` is the single upstream of every affordance below.
  const engineOwned = ENGINE_OWNED_EXITS.includes(op.automaticExit?.state ?? "");
  const exitLine = automaticExitLine(op);
  const attention =
    view.stopped &&
    !["unknown", "assets", "retired", "completed"].includes(view.mode) &&
    !engineOwned;
  const exhausted = view.mode === "exhausted";
  const request = useRef(0),
    flight = useRef(false),
    diagnosisRequest = useRef(0),
    diagnosisFlight = useRef<number | null>(null),
    mounted = useRef(true);
  const sharedPending = useRef(actionPending);
  sharedPending.current = actionPending;
  const busy = pending || quotePending || diagnosisPending || actionPending;
  const isSharedPending = () => sharedPending.current;
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const activeRequest = request;
    const timer = window.setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => {
      activeRequest.current++;
      window.clearInterval(timer);
    };
  }, []);
  const diagnosisSubject = JSON.stringify([
    diagnosisOperationKey(op),
    runtime?.data?.wallet.address ?? null,
    runtime?.data?.wallet.generation ?? null,
  ]);
  const diagnosisFence = JSON.stringify([
    diagnosisSubject,
    runtime?.executionReady === true,
    retry?.diagnosis?.allowed === true,
  ]);
  const currentDiagnosisFence = useRef(diagnosisFence);
  currentDiagnosisFence.current = diagnosisFence;
  const previousDiagnosisFence = useRef(diagnosisFence);
  useEffect(() => {
    if (previousDiagnosisFence.current === diagnosisFence) return;
    previousDiagnosisFence.current = diagnosisFence;
    if (diagnosisFlight.current !== null) {
      diagnosisRequest.current++;
      diagnosisFlight.current = null;
      setDiagnosisPending(false);
    }
  }, [diagnosisFence]);
  useEffect(() => {
    setDiagnosis(null);
    setDiagnosisError(null);
  }, [diagnosisSubject]);
  useEffect(() => {
    const activeRequest = diagnosisRequest;
    mounted.current = true;
    return () => {
      mounted.current = false;
      activeRequest.current++;
      diagnosisFlight.current = null;
    };
  }, []);
  const selected = /^\d+(\.\d{1,2})?$/.test(bps)
    ? Number(bps.split(".")[0]) * 100 + Number((bps.split(".")[1] ?? "").padEnd(2, "0"))
    : 0;
  const valid =
    Number.isInteger(selected) &&
    selected > (retry?.activeSlippageBps ?? 500) &&
    selected >= (retry?.requiredSlippageBps ?? (depositGuard ? 0 : 501)) &&
    selected <= (retry?.ceilingBps ?? 0);
  const higher = !viewer && attention && opening && !!retry?.retryWithSlippageAllowed && !exhausted;
  const same =
    !viewer && attention && !!retry?.sameLimits.allowed && !exhausted && (opening || !!onResume);
  const cancel = !viewer && attention && opening && !!retry?.cancel.allowed && !!onCancel;
  // "Cancel and set up again" opens the wizard, which an active autopilot run already blocks:
  // hidden here rather than shown disabled (design 47 / Phase 4 decision 6, [R2-3]).
  const autopilotActive = retry?.reasonCode === "AUTOPILOT_ACTIVE";
  const restart = cancel && !!op.opening?.prefill && !!onRestart && !exhausted && !autopilotActive;
  const recommended = higher
    ? "higher"
    : restart && recommendsSetup(failure)
      ? "restart"
      : same
        ? "same"
        : cancel
          ? "cancel"
          : "none";
  const reopenCapability = retry?.autoReopen ?? null;
  // The server capability is the only permission; a missing slippage is never treated as zero.
  const reopenOffered = cancel && !!reopenCapability;
  const reopenSlippageBps =
    typeof reopenCapability?.maxSlippageBps === "number" ? reopenCapability.maxSlippageBps : null;
  const reopen = reopenOffered && reopenCapability.allowed && reopenSlippageBps !== null;
  const retryChoice =
    (choice === "higher" && higher) ||
    (choice === "same" && same) ||
    (choice === "cancel" && cancel) ||
    (choice === "restart" && restart)
      ? choice
      : recommended;
  // The checkbox overrides the radio group without adding a fifth radio.
  const effectiveChoice = reopenChecked && reopen ? "reopen" : retryChoice;
  // Permission proofs and display snapshots are observations, not request identity.
  // Replace the object synchronously so even A → B → A cannot revive an old response.
  const quoteKey = JSON.stringify([
    quoteIdentity,
    runtime?.executionReady === true,
    actionPending,
    bps,
    choice || "higher",
  ]);
  const currentQuoteSubject = useRef({ key: quoteKey });
  if (currentQuoteSubject.current.key !== quoteKey) currentQuoteSubject.current = { key: quoteKey };
  const quoteSubject = currentQuoteSubject.current;
  const observation = quoteObservation?.subject === quoteSubject ? quoteObservation : null;
  const quote = observation?.quote ?? null;
  const quoteMatches = (candidate: RetryWithSlippageQuoteResponse) =>
    candidate.operationId === op.id &&
    candidate.priorIntentHash === op.intentRevision?.activeIntentHash &&
    candidate.maxSlippageBps === selected &&
    Date.parse(candidate.expiresAt) > Date.now();
  const invalidation = JSON.stringify([
    op.id,
    op.step,
    op.retry,
    op.opening,
    op.failure,
    op.intentRevision?.activeIntentHash,
    op.state,
    op.updatedAt,
    runtime?.executionReady,
    runtime?.data?.wallet.address,
    runtime?.data?.wallet.generation,
    actionPending,
  ]);
  useEffect(() => {
    request.current++;
    setReportedFailure(null);
    setError(null);
  }, [invalidation]);
  useEffect(() => {
    request.current++;
  }, [choice]);
  // A pinned reopen request belongs to one operation only; it never migrates to another card.
  useEffect(() => {
    reopenAttempt.current = null;
    setReopenUncertain(false);
    setReopenChecked(false);
  }, [op.id]);
  // The authoritative sequence view supersedes the pinned request and any uncertainty about it.
  const sequenceObserved = !!op.autoReopen;
  useEffect(() => {
    if (!sequenceObserved) return;
    reopenAttempt.current = null;
    setReopenUncertain(false);
  }, [sequenceObserved]);
  const chooseBps = (value: string) => {
    if (sharedPending.current) return;
    request.current++;
    setQuoteObservation(null);
    setBps(value);
    setError(null);
  };
  async function checkRetry() {
    if (
      diagnosisFlight.current !== null ||
      busy ||
      !runtime?.executionReady ||
      !retry?.diagnosis?.allowed
    )
      return;
    const generation = ++diagnosisRequest.current,
      fence = diagnosisFence,
      operationKey = diagnosisOperationKey(op);
    diagnosisFlight.current = generation;
    setDiagnosisPending(true);
    setDiagnosis(null);
    setDiagnosisError(null);
    try {
      const response = await api<RetryDiagnosisResponse>(
        `/operations/${encodeURIComponent(op.id)}/retry-diagnosis`,
        { method: "POST", sensitive: true, body: {} },
      );
      if (
        mounted.current &&
        generation === diagnosisRequest.current &&
        fence === currentDiagnosisFence.current &&
        operationKey === diagnosisOperationKey(response.operation)
      ) {
        const failure = diagnosisFailure(response);
        setDiagnosis({
          result:
            ["deposit-price-guard", "swap-output", "price-drift"].includes(response.result) &&
            !failure
              ? "unavailable"
              : response.result,
          message: response.message,
          checkedAt: failure?.observedAt ?? new Date().toISOString(),
          // Evidence precedence uses only server observations, never the browser receipt clock.
          failureBaselineAt: currentFailure(response.operation)?.observedAt ?? null,
          failure,
        });
        onChanged?.();
      }
    } catch (reason) {
      if (
        mounted.current &&
        generation === diagnosisRequest.current &&
        fence === currentDiagnosisFence.current
      )
        setDiagnosisError(reason instanceof Error ? reason.message : "Check unavailable");
    } finally {
      if (mounted.current && diagnosisFlight.current === generation) {
        diagnosisFlight.current = null;
        setDiagnosisPending(false);
      }
    }
  }
  async function prepareQuote() {
    if (
      quoteFlight.current ||
      flight.current ||
      busy ||
      !higher ||
      !valid ||
      !runtime?.executionReady
    )
      return;
    // Pin the actual click, including an untouched default, before proof polling removes suggestions.
    setChoice("higher");
    setBps(bps);
    const pinnedKey = JSON.stringify([quoteIdentity, true, actionPending, bps, "higher"]);
    if (currentQuoteSubject.current.key !== pinnedKey)
      currentQuoteSubject.current = { key: pinnedKey };
    const subject = currentQuoteSubject.current;
    const baseline = serverFailure?.observedAt ?? null;
    quoteFlight.current = subject;
    setQuotePending(true);
    setQuoteObservation(null);
    const isCurrent = () => mounted.current && currentQuoteSubject.current === subject;
    try {
      const response = await api<RetryWithSlippageQuoteResponse>(
        `/operations/${encodeURIComponent(op.id)}/retry-with-slippage/quote`,
        {
          method: "POST",
          body: { maxSlippageBps: selected, idempotencyKey: crypto.randomUUID() },
          sensitive: true,
        },
      );
      if (isCurrent()) {
        const matches = quoteMatches(response);
        setQuoteObservation({
          subject,
          quote: matches ? response : null,
          failure: null,
          failureBaselineAt: baseline,
          error: matches
            ? null
            : "The retry quote no longer matches this operation. Request a fresh quote.",
        });
      }
    } catch (reason) {
      if (isCurrent()) {
        setQuoteObservation({
          subject,
          quote: null,
          failureBaselineAt: baseline,
          error: reason instanceof Error ? reason.message : "Retry unavailable",
          failure: reason instanceof ApiError ? (reason.operationFailure ?? null) : null,
        });
        onChanged?.();
      }
    } finally {
      if (quoteFlight.current === subject) {
        quoteFlight.current = null;
        if (mounted.current) setQuotePending(false);
      }
    }
  }
  /** One click sends exactly one POST; an uncertain outcome is re-checked with the same request. */
  async function submitReopen() {
    if (
      flight.current ||
      quoteFlight.current ||
      diagnosisFlight.current !== null ||
      isSharedPending() ||
      !runtime?.executionReady
    )
      return;
    const attempt =
      reopenAttempt.current ??
      (reopenSlippageBps === null
        ? null
        : { idempotencyKey: crypto.randomUUID(), maxSlippageBps: reopenSlippageBps });
    if (!attempt) return;
    reopenAttempt.current = attempt;
    flight.current = true;
    setPending(true);
    setError(null);
    const generation = ++request.current;
    try {
      await api<AutoReopenResponse>(`/operations/${encodeURIComponent(op.id)}/auto-reopen`, {
        method: "POST",
        body: attempt,
        sensitive: true,
      });
      // The accepted request stays pinned until the server view shows the sequence, so a further
      // click before that observation replays the same key instead of minting a second one.
      if (mounted.current) {
        setReopenUncertain(false);
        setReopenChecked(false);
      }
      if (generation === request.current && !sharedPending.current) onChanged?.();
    } catch (reason) {
      // Only a deliberate refusal (an HTTP status below 500) proves the request was not admitted;
      // any 5xx, including a coded INTERNAL_ERROR, leaves the outcome unknown.
      const lost = !(reason instanceof ApiError) || reason.status >= 500;
      if (!lost) reopenAttempt.current = null;
      if (mounted.current) {
        setReopenUncertain(lost);
        setError(
          lost ? null : reason instanceof Error ? reason.message : "Cancel and reopen unavailable",
        );
        onChanged?.();
      }
    } finally {
      flight.current = false;
      setPending(false);
    }
  }
  async function act() {
    if (
      flight.current ||
      quoteFlight.current ||
      diagnosisFlight.current !== null ||
      isSharedPending() ||
      !runtime?.executionReady
    )
      return;
    if (effectiveChoice === "reopen") {
      await submitReopen();
      return;
    }
    if (effectiveChoice === "cancel") {
      onCancel?.();
      return;
    }
    if (effectiveChoice === "restart") {
      onRestart?.();
      return;
    }
    if (effectiveChoice === "same" && !opening) {
      onResume?.(op);
      return;
    }
    if (effectiveChoice === "none" || (effectiveChoice === "higher" && !valid)) return;
    if (effectiveChoice === "higher") {
      if (!higher) return;
      if (!quote || !quoteMatches(quote)) {
        await prepareQuote();
        return;
      }
    }
    flight.current = true;
    setPending(true);
    setError(null);
    const generation = ++request.current;
    const subject = effectiveChoice === "higher" ? quoteSubject : null;
    const isCurrent = () =>
      mounted.current &&
      (subject ? currentQuoteSubject.current === subject : generation === request.current);
    try {
      if (effectiveChoice === "same") {
        await api(`/operations/${encodeURIComponent(op.id)}/resume`, {
          method: "POST",
          body: op.opening?.absenceContinuation
            ? { activeIntentHash: op.intentRevision?.activeIntentHash }
            : {},
          sensitive: true,
        });
        if (generation === request.current && !sharedPending.current) onChanged?.();
      } else if (quote && higher && valid && quoteMatches(quote)) {
        // Submission consumes the quote; observation-only polls must not discard its outcome.
        setQuoteObservation(null);
        await api<RetryWithSlippageResponse>(
          `/operations/${encodeURIComponent(op.id)}/retry-with-slippage`,
          {
            method: "POST",
            body: { maxSlippageBps: quote.maxSlippageBps, idempotencyKey: quote.idempotencyKey },
            sensitive: true,
          },
        );
        if (isCurrent() && !sharedPending.current) onChanged?.();
      }
    } catch (reason) {
      if (isCurrent()) {
        setError(reason instanceof Error ? reason.message : "Retry unavailable");
        setReportedFailure(reason instanceof ApiError ? (reason.operationFailure ?? null) : null);
        setQuoteObservation(null);
        onChanged?.();
      }
    } finally {
      flight.current = false;
      setPending(false);
    }
  }
  const price = attention && failure?.code === "PRICE_CHANGED" ? failure.priceChanged : null;
  const priceTokens = [price?.inputToken, price?.outputToken].filter(
    (token): token is NonNullable<typeof token> => token != null,
  );
  const currentPriceDisplay = price?.currentPrice
    ? orientedPrice(price.currentPrice, priceTokens)
    : null;
  const candidateFailure = observation?.failure ?? reportedFailure;
  const candidateIsCurrent =
    candidateFailure &&
    currentFailure(op, candidateFailure) &&
    (!serverFailure ||
      Date.parse(candidateFailure.observedAt) >= Date.parse(serverFailure.observedAt)) &&
    (!observation ||
      !serverFailure ||
      (observation.failureBaselineAt !== null &&
        Date.parse(observation.failureBaselineAt) >= Date.parse(serverFailure.observedAt)));
  const candidateExplanation = candidateIsCurrent
    ? failurePresentation(op, candidateFailure)
    : null;
  const requestError = observation?.error ?? error;
  const diagnosticError = candidateExplanation ? requestError : null;
  const presetBps = retry
    ? [50, 100, 200, 300, 400, 500].filter(
        (value) =>
          value > retry.activeSlippageBps &&
          value >= (retry.requiredSlippageBps ?? (depositGuard ? 0 : 501)) &&
          value <= retry.ceilingBps,
      )
    : [];
  const customSlippageSelected =
    bps !== "" && !presetBps.some((value) => bps === formatNative(BigInt(value), 2));
  const steps = operationSteps(op, view);
  const routeLine = routeFailureLine(op);
  const reviewing =
    higher &&
    valid &&
    runtime?.executionReady &&
    !actionPending &&
    effectiveChoice === "higher" &&
    quote &&
    quoteMatches(quote) &&
    Date.parse(quote.expiresAt) > now;
  const sameLabel = !opening
    ? "Resume"
    : op.opening?.absenceContinuation
      ? "Confirm retry, same limits"
      : failure?.code === "QUOTE_EXPIRED"
        ? "Continue, same limits"
        : "Retry, same limits";
  const sequence = op.autoReopen ?? null;
  const sequenceReplacesView = replacesCleanupSuccess(op);
  return (
    <div className="compact-operation">
      <ol className="operation-stepper">
        {steps.map(({ label, state, icon }) => (
          <li key={label} className={state}>
            <span aria-hidden="true">{icon}</span>
            {label}
            {state === "skipped" && <small>not required</small>}
          </li>
        ))}
      </ol>
      {routeLine !== null && (
        <p className="route-failures" style={automaticExitStyle}>
          {routeLine}
        </p>
      )}
      {sequence && <AutoReopenProgress sequence={sequence} />}
      <div className={`opening-problem ${view.tone}`}>
        {!sequenceReplacesView && (
          <div className="operation-message">
            <span
              className={`operation-message-icon ${view.mode === "progress" ? "spinner" : ""}`}
              aria-hidden="true"
            >
              {view.mode === "progress" ? "" : view.icon}
            </span>
            <div>
              <strong style={{ fontSize: 14 }}>
                {reviewing
                  ? depositGuard
                    ? "Review updated conditions"
                    : "Review the new minimum"
                  : view.title}
              </strong>
              <p>
                {reviewing
                  ? "Confirm to retry swap & deposit with these conditions. Changing the choice or waiting past the timer needs a new quote."
                  : view.message}
              </p>
              {/* What the ENGINE will do next, independent of `view.mode`: the chip above belongs
                  to whichever branch matched, and for a retrying or observing exit that branch is
                  one that knows nothing about automatic exits. This line is the only place the
                  attempt count appears, so it must not depend on the branch order. */}
              {exitLine !== null && (
                <p className="automatic-exit" style={automaticExitStyle}>
                  {exitLine}
                </p>
              )}
            </div>
          </div>
        )}
        {view.mode === "unknown" && <p>Retry and Cancel appear when the result is known.</p>}
        {op.opening?.absenceContinuation && (
          <div>
            <p>{op.opening.absenceContinuation.explanation}</p>
            <p>
              Remaining signed-attempt budget: {op.opening.absenceContinuation.remainingAttempts}.
              Approval expires: {op.opening.absenceContinuation.expiresAt}.
              {op.opening.absenceContinuation.state === "consent-required" &&
                " Confirm retry renews approval for 10 minutes with the same limits, position and remaining budget."}
            </p>
          </div>
        )}
        {attention && depositGuard && retry && !reviewing && (
          <p>
            Approved slippage: {bpsLabel(retry.activeSlippageBps)}. A higher setting needs a fresh
            quote and simulation; it is not guaranteed to pass.
          </p>
        )}
        {attention &&
          !viewer &&
          retry?.diagnosis?.allowed &&
          (!depositGuard || !op.opening?.verifiedEmptyAt) && (
            <button
              className="button"
              disabled={busy || !runtime?.executionReady}
              onClick={() => void checkRetry()}
            >
              {diagnosisPending
                ? "Checking…"
                : currentDiagnosis || op.opening?.absenceContinuation
                  ? "Check again"
                  : "Check retry conditions"}
            </button>
          )}
        {attention && currentDiagnosis && (
          <div role="status">
            <strong>
              {currentDiagnosis.result === "passed"
                ? "Latest check passed"
                : currentDiagnosis.result === "unavailable"
                  ? "Latest check unavailable"
                  : "Latest check found a changed condition"}
            </strong>
            <span> · Checked {formatWhen(currentDiagnosis.checkedAt)}</span>
            <p>{currentDiagnosis.message}</p>
            {currentDiagnosis.result === "passed" && serverFailure && (
              <small>
                The failure in Details is historical; current actions still come from the server.
              </small>
            )}
            {!["passed", "unavailable"].includes(currentDiagnosis.result) && (
              <small>This check did not submit a transaction.</small>
            )}
          </div>
        )}
        {attention && diagnosisError && (
          <p role="alert" className="inline-alert warning">
            {diagnosisError}
          </p>
        )}
        {price && price.reason !== "PRICE_DRIFT" && !reviewing && (
          <dl className="opening-facts">
            <div>
              <dt style={factLabelStyle}>Approved minimum</dt>
              <dd className="num" style={factValueStyle}>
                {price.decimalAmounts.approvedMinimum === null
                  ? "—"
                  : `${formatToken(price.decimalAmounts.approvedMinimum)} ${price.outputToken?.symbol ?? ""}`}
              </dd>
              <small style={factNoteStyle}>
                {price.approvedSlippageBps === null
                  ? "estimate unavailable"
                  : `at ${bpsLabel(price.approvedSlippageBps)}`}
              </small>
            </div>
            <div>
              <dt style={factLabelStyle}>Current expected</dt>
              <dd className="num" style={factValueStyle}>
                {price.decimalAmounts.currentExpected === null
                  ? "—"
                  : `${formatToken(price.decimalAmounts.currentExpected)} ${price.outputToken?.symbol ?? ""}`}
              </dd>
              <small style={factNoteStyle}>
                {price.decimalAmounts.currentExpected === null
                  ? "estimate unavailable"
                  : "server quote"}
              </small>
            </div>
            {retry && (
              <div>
                <dt style={factLabelStyle}>Needs slippage</dt>
                <dd className="num" style={factValueStyle}>
                  {retry.requiredSlippageBps === null ? "—" : bpsLabel(retry.requiredSlippageBps)}
                </dd>
                <small style={factNoteStyle}>
                  {retry.requiredSlippageBps === null ? "estimate unavailable" : "or higher"}
                </small>
              </div>
            )}
            {price.currentPrice && (
              <div>
                <dt style={factLabelStyle}>Price now</dt>
                <dd className="num" style={factValueStyle}>
                  {currentPriceDisplay
                    ? `${formatPrice(currentPriceDisplay.price)} ${currentPriceDisplay.unit}`
                    : "—"}
                </dd>
              </div>
            )}
          </dl>
        )}
        {attention &&
          !reviewing &&
          (failure?.marketMovement ? (
            <MovementFacts movement={failure.marketMovement} opening={op.opening} />
          ) : depositGuard ||
            failure?.code === "PRICE_CHANGED" ||
            failure?.code === "PRICE_IMPACT" ? (
            <p className="muted">
              Market movement unavailable: no validated pre-simulation pool read.
            </p>
          ) : null)}
        {price?.reason === "PRICE_DRIFT" && !reviewing && (
          <dl className="opening-facts">
            {price.priceDrift && (
              <>
                <Fact label="Price drift" value={percentLabel(price.priceDrift.changePercent)} />
                <Fact
                  label="Approved price-impact limit"
                  value={bpsLabel(price.priceDrift.limitBps)}
                />
              </>
            )}
            {price.currentPrice && (
              <Fact
                label="Price at check"
                value={
                  currentPriceDisplay
                    ? `${formatPrice(currentPriceDisplay.price)} ${currentPriceDisplay.unit}`
                    : "—"
                }
              />
            )}
            {price.observedAt && <Fact label="Checked at" value={formatWhen(price.observedAt)} />}
          </dl>
        )}
        {attention &&
          opening &&
          op.opening?.executionVersion === 2 &&
          op.opening.verifiedEmptyAt &&
          op.opening.setupState === "finalized-success" &&
          !op.opening.fundingSwapState && (
            <p className="operation-funds">
              Nothing was swapped or deposited. Setup rent and network fees have already been paid.
            </p>
          )}
        {attention && opening && op.opening?.fundingSwapState === "finalized-success" && (
          <p className="operation-funds">
            The funding swap already completed. Its effects remain even though the deposit stopped.
          </p>
        )}
        {attention && !opening && op.step === "exit" && (
          <p className="operation-funds">
            Liquidity was already withdrawn. The return swap has not completed.
          </p>
        )}
        {op.residuals?.length ? (
          <div aria-label="Recorded residuals">
            {op.residuals.map((residual) => (
              <p className="amount-line num" key={`${residual.mint}:${residual.amountNative}`}>
                Token <span title={residual.mint}>{shorten(residual.mint, 7)}</span> ·{" "}
                {residual.amountNative} native units
              </p>
            ))}
          </div>
        ) : null}
        {view.mode === "paused" && (
          <button
            className="button ghost"
            onClick={() => window.dispatchEvent(new Event("auto-pools:show-system-status"))}
          >
            System status
          </button>
        )}
        {attention && (
          <div className="opening-choices">
            {!reviewing && (
              <>
                {higher && (
                  <label className={effectiveChoice === "higher" ? "selected" : ""}>
                    <span className="opening-choice-heading">
                      <input
                        type="radio"
                        name={`retry-${op.id}`}
                        checked={effectiveChoice === "higher"}
                        disabled={busy}
                        onChange={() => {
                          if (busy) return;
                          setChoice("higher");
                        }}
                      />
                      <span className="opening-choice-title">
                        Retry with higher slippage
                        <span
                          style={{
                            padding: "1px 6px",
                            borderRadius: "var(--r-bar)",
                            background: "var(--pos-bg)",
                            color: "var(--pos-text)",
                            fontSize: 11,
                            fontWeight: 500,
                          }}
                        >
                          Recommended
                        </span>
                      </span>
                    </span>
                    <small>
                      {depositGuard
                        ? "Review a wider deposit price range and a lower swap minimum."
                        : "Accept a slightly lower amount."}
                    </small>
                    <div className="slippage-presets">
                      <span className="segmented slippage-segmented">
                        {presetBps.map((b) => (
                          <button
                            key={b}
                            type="button"
                            className={`button small ${bps === formatNative(BigInt(b), 2) ? "selected" : ""}`}
                            aria-pressed={bps === formatNative(BigInt(b), 2)}
                            disabled={busy}
                            onClick={() => {
                              if (busy) return;
                              setChoice("higher");
                              chooseBps(formatNative(BigInt(b), 2));
                            }}
                          >
                            {bpsLabel(b)}
                          </button>
                        ))}
                      </span>
                      <input
                        className={customSlippageSelected ? "selected" : ""}
                        aria-label="Custom slippage percent"
                        placeholder="Custom %"
                        value={bps}
                        disabled={busy}
                        inputMode="decimal"
                        onChange={(e) => {
                          if (busy) return;
                          setChoice("higher");
                          chooseBps(e.target.value);
                        }}
                      />
                    </div>
                    <small>
                      {depositGuard
                        ? "Next whole-percent setting to review; required slippage is unknown and success is not guaranteed"
                        : "needs at least"}{" "}
                      {retry.requiredSlippageBps === null
                        ? depositGuard
                          ? ""
                          : "— · estimate unavailable"
                        : bpsLabel(retry.requiredSlippageBps)}{" "}
                      · max {bpsLabel(retry.ceilingBps)}
                    </small>
                  </label>
                )}
                {same && (
                  <label className={effectiveChoice === "same" ? "selected" : ""}>
                    <span className="opening-choice-heading">
                      <input
                        type="radio"
                        name={`retry-${op.id}`}
                        checked={effectiveChoice === "same"}
                        disabled={busy}
                        onChange={() => {
                          if (busy) return;
                          setChoice("same");
                        }}
                      />{" "}
                      {sameLabel}
                    </span>
                    <small>
                      {failure?.priceChanged?.reason === "PRICE_DRIFT"
                        ? "The price must return within the original approved tolerance."
                        : "Keep the current approved minimum."}
                    </small>
                  </label>
                )}
                {cancel && (
                  <label className={effectiveChoice === "cancel" ? "selected" : ""}>
                    <span className="opening-choice-heading">
                      <input
                        type="radio"
                        name={`retry-${op.id}`}
                        checked={effectiveChoice === "cancel"}
                        disabled={busy}
                        onChange={() => {
                          if (busy) return;
                          setChoice("cancel");
                        }}
                      />
                      <span style={{ color: "var(--neg-text)" }}>Cancel opening</span>
                    </span>
                    <small>Close the empty account.</small>
                  </label>
                )}
                {restart && (
                  <label className={effectiveChoice === "restart" ? "selected" : ""}>
                    <span className="opening-choice-heading">
                      <input
                        type="radio"
                        name={`retry-${op.id}`}
                        checked={effectiveChoice === "restart"}
                        disabled={busy}
                        onChange={() => {
                          if (busy) return;
                          setChoice("restart");
                        }}
                      />
                      <span className="opening-choice-title">
                        Cancel and set up again
                        {recommended === "restart" && (
                          <span
                            style={{
                              padding: "1px 6px",
                              borderRadius: "var(--r-bar)",
                              background: "var(--pos-bg)",
                              color: "var(--pos-text)",
                              fontSize: 11,
                              fontWeight: 500,
                            }}
                          >
                            Recommended
                          </span>
                        )}
                      </span>
                    </span>
                    {recommended === "restart" && (
                      <small>
                        Review amount, range and limits. Cleanup and setup may require network fees.
                      </small>
                    )}
                  </label>
                )}
              </>
            )}
            {reviewing && (
              <section
                className="retry-review"
                aria-label="Review retry minimum"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                  gridColumn: "1 / -1",
                  minWidth: 0,
                  padding: 12,
                  border: "1px solid var(--text)",
                  borderRadius: "var(--r-inner)",
                }}
              >
                <div className="retry-review-heading">
                  <strong>Retry with {bpsLabel(quote.maxSlippageBps)} slippage</strong>
                  <span
                    className="quote-validity"
                    style={{
                      display: "inline-flex",
                      padding: "2px 8px",
                      borderRadius: 999,
                      background: "var(--pos-bg)",
                      color: "var(--pos-text)",
                      fontSize: 12,
                    }}
                  >
                    Quote valid{" "}
                    <span className="num">
                      {Math.floor(
                        Math.max(0, Math.ceil((Date.parse(quote.expiresAt) - now) / 1000)) / 60,
                      )}
                      :
                      {String(
                        Math.max(0, Math.ceil((Date.parse(quote.expiresAt) - now) / 1000)) % 60,
                      ).padStart(2, "0")}
                    </span>
                  </span>
                </div>
                {quote.marketMovement && (
                  <MovementFacts movement={quote.marketMovement} opening={op.opening} />
                )}
                <dl className="opening-facts">
                  <div>
                    <dt style={factLabelStyle}>New minimum</dt>
                    <dd className="num" style={reviewFactValueStyle}>
                      {quote.newMinimum.decimal} {quote.outputToken.symbol}
                    </dd>
                    <small style={factNoteStyle}>from the server quote</small>
                  </div>
                  <div>
                    <dt style={factLabelStyle}>Was</dt>
                    <dd className="num" style={reviewFactValueStyle}>
                      {quote.oldMinimum.decimal} {quote.outputToken.symbol}
                    </dd>
                    <small style={factNoteStyle}>
                      at {bpsLabel(retry.activeSlippageBps)} slippage
                    </small>
                  </div>
                  <div>
                    <dt style={factLabelStyle}>Other limits</dt>
                    <dd style={reviewFactValueStyle}>Unchanged</dd>
                    <small style={factNoteStyle}>fees, rent, reserve, impact</small>
                  </div>
                </dl>
                {quote.guardReview &&
                  (() => {
                    const guardReview = quote.guardReview;
                    const tokens = [
                      { mint: quote.currentPrice.baseMint, symbol: guardReview.baseSymbol },
                      { mint: quote.currentPrice.quoteMint, symbol: guardReview.quoteSymbol },
                    ];
                    const oldBounds = orientedBounds(
                      {
                        lower: guardReview.oldMinimumPrice,
                        upper: guardReview.oldMaximumPrice,
                        ...quote.currentPrice,
                      },
                      tokens,
                    );
                    const newBounds = orientedBounds(
                      {
                        lower: guardReview.newMinimumPrice,
                        upper: guardReview.newMaximumPrice,
                        ...quote.currentPrice,
                      },
                      tokens,
                    );
                    return (
                      <div>
                        <p>Deposit price bounds ({oldBounds?.unit ?? newBounds?.unit ?? "—"}):</p>
                        <p className="num">
                          {oldBounds
                            ? `${formatPrice(oldBounds.lower)} – ${formatPrice(oldBounds.upper)}`
                            : "—"}{" "}
                          →{" "}
                          {newBounds
                            ? `${formatPrice(newBounds.lower)} – ${formatPrice(newBounds.upper)}`
                            : "—"}
                        </p>
                        <p>
                          Total slippage: {bpsLabel(retry.activeSlippageBps)} →{" "}
                          {bpsLabel(quote.maxSlippageBps)}
                        </p>
                        <details>
                          <summary>Deposit guard details</summary>
                          <p>
                            Minimum liquidity (native): {guardReview.oldMinimumLiquidity} →{" "}
                            {guardReview.newMinimumLiquidity}
                          </p>
                        </details>
                      </div>
                    );
                  })()}
              </section>
            )}
          </div>
        )}
        {attention && reopenOffered && !reviewing && (
          <div className="auto-reopen-choice">
            {reopen ? (
              <>
                <label className="opening-choice-heading">
                  <input
                    type="checkbox"
                    checked={effectiveChoice === "reopen"}
                    disabled={busy}
                    onChange={(event) => {
                      if (busy) return;
                      setReopenChecked(event.target.checked);
                    }}
                  />
                  <span className="opening-choice-title">Reopen around current price</span>
                </label>
                <small>
                  Cancels this opening and opens the same amount around the current price at up to{" "}
                  {bpsLabel(reopenSlippageBps)} slippage. The server runs the whole sequence;
                  success is not guaranteed.
                </small>
              </>
            ) : (
              <p className="operation-action-reason">
                {reopenCapability.allowed && reopenSlippageBps === null
                  ? "The approved reopen slippage is unavailable, so reopening cannot be requested."
                  : (reopenCapability.reason ??
                    "Reopening around the current price is unavailable for this opening.")}
              </p>
            )}
          </div>
        )}
        {reopenUncertain && !sequence && (
          <div role="alert" className="inline-alert warning">
            <strong>Submission outcome is uncertain</strong>
            <p>
              The reopen request may or may not have been accepted. Check again with the same
              request; it cannot start a second sequence.
            </p>
            <button
              className="button"
              disabled={busy || !runtime?.executionReady}
              onClick={() => void submitReopen()}
            >
              Check again
            </button>
          </div>
        )}
        {attention && opening && !higher && retry?.reason && <small>{retry.reason}</small>}
        {attention && retry && !retry.sameLimits.allowed && (
          // Only the server capability can explain why retry is unavailable.
          <p className="operation-action-reason">
            {retry.sameLimits.reason ?? "Resuming is unavailable for this operation."}
          </p>
        )}
        {attention && !viewer && !cancel && opening && (
          <p className="operation-action-reason">
            {retry?.cancel.reason ??
              "Cancellation is unavailable until empty setup and settled results are verified."}
          </p>
        )}
        {attention && !retry && (
          <p className="operation-action-reason">
            The server has not provided a retry capability for this operation.
          </p>
        )}
        {attention && !runtime?.executionReady && effectiveChoice !== "none" && (
          <p>Executor unavailable. Check system status before continuing.</p>
        )}
        {quotePending && quoteFlight.current === quoteSubject && (
          <p role="status">Preparing updated conditions…</p>
        )}
        {candidateExplanation && requestError && (
          <div role={serverFailure ? "alert" : "status"} className="inline-alert warning">
            <strong>Updated conditions unavailable · {candidateExplanation[0]}</strong>
            <p>{candidateExplanation[1]}</p>
          </div>
        )}
        {requestError && !diagnosticError && (
          <p role="alert" className="inline-alert warning">
            {observation ? "Updated conditions unavailable: " : ""}
            {requestError}
          </p>
        )}
      </div>
      {attention && (
        <footer className="opening-cta">
          <small>
            Attempt {op.attempts} of {op.maxAttempts ?? "—"}
          </small>
          {reviewing && (
            <button
              className="button"
              disabled={busy}
              onClick={() => {
                request.current++;
                setQuoteObservation(null);
              }}
            >
              Back
            </button>
          )}
          {effectiveChoice !== "none" && (
            <button
              key={effectiveChoice}
              className={`button ${["cancel", "reopen"].includes(effectiveChoice) ? "danger" : "primary"}`}
              disabled={
                busy || !runtime?.executionReady || (effectiveChoice === "higher" && !valid)
              }
              onClick={() => void act()}
            >
              {effectiveChoice === "reopen"
                ? pending
                  ? "Submitting…"
                  : "Cancel and reopen"
                : pending || quotePending
                  ? "Preparing…"
                  : effectiveChoice === "higher"
                    ? reviewing
                      ? `Confirm retry with ${bpsLabel(quote.maxSlippageBps)}`
                      : depositGuard
                        ? "Review updated conditions"
                        : "Review new minimum"
                    : effectiveChoice === "same"
                      ? sameLabel
                      : effectiveChoice === "restart"
                        ? "Cancel and set up again"
                        : "Cancel opening"}
            </button>
          )}
        </footer>
      )}
      {!attention && (
        <footer className="opening-cta">
          <small>
            Attempt {op.attempts} of {op.maxAttempts ?? "—"}
          </small>
          {view.mode === "waiting" && op.nextAttemptAt ? (
            <small>Next check {formatWhen(op.nextAttemptAt)} · automatic</small>
          ) : (
            <small>Updated {formatAgo(op.updatedAt)}</small>
          )}
        </footer>
      )}
      <details>
        <summary>Details</summary>
        <p>
          Step: {op.step || "—"} · attempts: {op.attempts} · max {op.maxAttempts ?? "—"}
        </p>
        <p>
          State: {op.state} · Updated {formatWhen(op.updatedAt)}
        </p>
        {op.nextAttemptAt && <p>Next check {formatWhen(op.nextAttemptAt)}</p>}
        {op.positionId && <p className="num">Position {op.positionId}</p>}
        {op.signature && <p className="num">Signature {op.signature}</p>}
        {op.failure && (
          <p>
            {op.failure.code} · {op.failure.step}
          </p>
        )}
        {op.lastError && <p>{op.lastError}</p>}
        {retry?.reason && <p>{retry.reason}</p>}
        {diagnosticError && <p>{diagnosticError}</p>}
        {op.opening?.funding && (op.opening.expenseBudget ?? op.opening.totalBudget) && (
          <section className="opening-budget" aria-label="Original opening budget">
            <strong className="opening-budget-heading">Original opening budget</strong>
            <dl className="opening-budget-facts">
              <Fact
                label="Principal"
                value={`${formatToken(op.opening.funding.decimal)} ${op.opening.prefill?.tokenA.mint === op.opening.funding.mint ? op.opening.prefill.tokenA.symbol : op.opening.prefill?.tokenB.mint === op.opening.funding.mint ? op.opening.prefill.tokenB.symbol : shorten(op.opening.funding.mint, 7)}`}
                note={formatFundingUsd(op.opening.funding.usd)}
              />
              {op.opening.expenseBudget && (
                <Fact
                  label="Maximum network-fee + rent budget"
                  value={`${formatToken(op.opening.expenseBudget.decimal)} SOL`}
                  note={formatFundingUsd(op.opening.expenseBudget.usd)}
                />
              )}
              {op.opening.totalBudget && (
                <Fact
                  label="Original principal + expense-budget total"
                  value={formatFundingUsd(op.opening.totalBudget.usd)}
                  note={op.opening.totalBudget.amounts
                    .map(
                      (amount) =>
                        `${formatToken(amount.decimal)} ${amount.mint === CANONICAL_MINTS.SOL ? "SOL" : op.opening?.prefill?.tokenA.mint === amount.mint ? op.opening.prefill.tokenA.symbol : (op.opening?.prefill?.tokenB.symbol ?? shorten(amount.mint, 7))}`,
                    )
                    .join(" + ")}
                />
              )}
            </dl>
            <p className="opening-budget-note">
              These are original maximum allowances, not actual spending or remaining budget.
            </p>
          </section>
        )}
      </details>
    </div>
  );
}
